// ============================================================
// MySQLMiniPracticalProject.js  ✅ (single page)
// COMPLETE SOLUTION — MySQL Capstone Assignment
// Covers Full MySQL Curriculum + DBA Responsibilities
//
// Notes for junior devs:
// - This page is intentionally "all-in-one" to match the request.
// - In production, you would split it into smaller components.
// - All SQL is written to be readable, safe, and DBA-aligned.
// ============================================================

import React, { useMemo, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    Paper,
    Chip,
    Stack,
    Tabs,
    Tab,
    Button,
    Tooltip,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.70)",
    codeBg: "rgba(0,0,0,0.55)",
    codeText: "#FFD700",
};

/* ============================================================
   Small helper: CodeBlock with copy button (DRY)
============================================================ */
function CodeBlock({ title, subtitle, code }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // If clipboard fails (rare), just ignore.
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2.5,
                mb: 3,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
            >
                <Box>
                    <Typography sx={{ color: BRAND.white, fontWeight: 800, fontSize: 15 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Tooltip title={copied ? "Copied!" : "Copy SQL"} placement="top">
                    <Button
                        size="small"
                        onClick={copy}
                        startIcon={copied ? <DoneIcon /> : <ContentCopyIcon />}
                        sx={{
                            bgcolor: BRAND.goldGlow,
                            color: BRAND.goldBright,
                            border: `1px solid ${BRAND.border}`,
                            textTransform: "none",
                            "&:hover": { bgcolor: "rgba(218,165,32,0.22)" },
                        }}
                    >
                        {copied ? "Copied" : "Copy"}
                    </Button>
                </Tooltip>
            </Stack>

            <Box
                sx={{
                    background: BRAND.codeBg,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 2,
                    p: 2,
                    fontFamily: "monospace",
                    color: BRAND.codeText,
                    whiteSpace: "pre-wrap",
                    fontSize: 13,
                    lineHeight: 1.65,
                }}
            >
                {code}
            </Box>
        </Paper>
    );
}

/* ============================================================
   MAIN PAGE
============================================================ */
export default function MySQLProjectPractical() {
    const [tab, setTab] = useState(0);

    // All solution SQL blocks kept in one place (clean code)
    const blocks = useMemo(
        () => [
            {
                label: "1) Schema",
                title: "1️⃣ Database & Table Design (Normalized Schema + Constraints)",
                subtitle:
                    "Creates academy_pro, tables, keys, constraints, and DBA-friendly defaults.",
                code: `-- ✅ 1) DATABASE
CREATE DATABASE IF NOT EXISTS academy_pro;
USE academy_pro;

-- ✅ 2) USERS (students/admins)
-- DBA note: email is UNIQUE; role is ENUM for controlled values.
CREATE TABLE IF NOT EXISTS users (
  id INT PRIMARY KEY AUTO_INCREMENT,
  full_name VARCHAR(100) NOT NULL,
  email VARCHAR(120) NOT NULL UNIQUE,
  role ENUM('STUDENT','ADMIN') DEFAULT 'STUDENT',
  is_active BOOLEAN DEFAULT TRUE,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
) ENGINE=InnoDB;

-- ✅ 3) COURSES
CREATE TABLE IF NOT EXISTS courses (
  id INT PRIMARY KEY AUTO_INCREMENT,
  title VARCHAR(120) NOT NULL,
  price DECIMAL(8,2) NOT NULL CHECK (price >= 0),
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
) ENGINE=InnoDB;

-- ✅ 4) ENROLLMENTS (many-to-many)
-- Composite PK prevents duplicate enrollments.
CREATE TABLE IF NOT EXISTS enrollments (
  user_id INT NOT NULL,
  course_id INT NOT NULL,
  enrolled_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (user_id, course_id),
  CONSTRAINT fk_enroll_user FOREIGN KEY (user_id) REFERENCES users(id)
    ON DELETE CASCADE ON UPDATE CASCADE,
  CONSTRAINT fk_enroll_course FOREIGN KEY (course_id) REFERENCES courses(id)
    ON DELETE RESTRICT ON UPDATE CASCADE
) ENGINE=InnoDB;

-- ✅ 5) PAYMENTS
-- DBA note: Store amounts as DECIMAL, never FLOAT.
CREATE TABLE IF NOT EXISTS payments (
  id INT PRIMARY KEY AUTO_INCREMENT,
  user_id INT NOT NULL,
  amount DECIMAL(10,2) NOT NULL CHECK (amount >= 0),
  status ENUM('PENDING','PAID','FAILED','REFUNDED') DEFAULT 'PAID',
  paid_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  CONSTRAINT fk_pay_user FOREIGN KEY (user_id) REFERENCES users(id)
    ON DELETE RESTRICT ON UPDATE CASCADE
) ENGINE=InnoDB;`,
            },

            {
                label: "2) Seed",
                title: "2️⃣ Insert Sample Data (Realistic Seed Dataset)",
                subtitle:
                    "Seed users, courses, enrollments, payments for testing queries & reports.",
                code: `USE academy_pro;

-- USERS
INSERT INTO users (full_name, email, role) VALUES
('Ada Lovelace', 'ada@academy.com', 'ADMIN'),
('Alan Turing', 'alan@academy.com', 'STUDENT'),
('Grace Hopper', 'grace@academy.com', 'STUDENT'),
('Tim Berners-Lee', 'tim@academy.com', 'STUDENT');

-- COURSES
INSERT INTO courses (title, price) VALUES
('SQL Fundamentals', 99.99),
('Joins & Data Modeling', 129.99),
('Indexes & Optimization', 149.99),
('Transactions & ACID', 159.99);

-- ENROLLMENTS (many-to-many)
INSERT INTO enrollments (user_id, course_id) VALUES
(2, 1), (2, 2),
(3, 1), (3, 3),
(4, 1), (4, 2), (4, 4);

-- PAYMENTS
INSERT INTO payments (user_id, amount, status) VALUES
(2, 229.98, 'PAID'),
(3, 249.98, 'PAID'),
(4, 389.97, 'PAID');`,
            },

            {
                label: "3) CRUD",
                title: "3️⃣ CRUD Operations (SELECT / INSERT / UPDATE / DELETE)",
                subtitle:
                    "Safe patterns: preview with SELECT, constrain with WHERE, use transactions for risky changes.",
                code: `USE academy_pro;

-- READ (SELECT)
SELECT id, full_name, email, role, created_at
FROM users
ORDER BY created_at DESC;

SELECT id, title, price
FROM courses
ORDER BY price DESC;

-- CREATE (INSERT)
INSERT INTO users (full_name, email, role)
VALUES ('Margaret Hamilton', 'margaret@academy.com', 'STUDENT');

-- UPDATE (ALWAYS USE WHERE)
-- ✅ Preview first:
SELECT * FROM users WHERE email = 'alan@academy.com';

-- ✅ Update safely:
UPDATE users
SET is_active = TRUE
WHERE email = 'alan@academy.com';

-- DELETE (SAFE: use transaction)
START TRANSACTION;

-- ✅ Preview the target
SELECT * FROM users WHERE email='ghost@academy.com';

-- ✅ Only delete the specific row (if exists)
DELETE FROM users WHERE email='ghost@academy.com';

-- ✅ Confirm / Undo
COMMIT;  -- or ROLLBACK;`,
            },

            {
                label: "4) Filters",
                title: "4️⃣ Filtering, Sorting & Functions",
                subtitle:
                    "WHERE / ORDER BY / LIMIT + COUNT, SUM, AVG + GROUP BY / HAVING for reporting.",
                code: `USE academy_pro;

-- Filtering + sorting
SELECT * FROM courses
WHERE price > 120
ORDER BY price DESC;

-- Aggregates
SELECT COUNT(*) AS total_users FROM users;
SELECT COUNT(*) AS total_courses FROM courses;

SELECT SUM(amount) AS total_revenue
FROM payments
WHERE status = 'PAID';

SELECT AVG(price) AS avg_course_price
FROM courses;

-- Group reports: revenue per user
SELECT u.full_name, SUM(p.amount) AS revenue
FROM users u
JOIN payments p ON p.user_id = u.id
WHERE p.status = 'PAID'
GROUP BY u.full_name
HAVING revenue > 200
ORDER BY revenue DESC;`,
            },

            {
                label: "5) Joins",
                title: "5️⃣ Joins & Relationships (1–Many + Many–Many)",
                subtitle:
                    "INNER JOIN for enrolled users, LEFT JOIN for courses with/without enrollments.",
                code: `USE academy_pro;

-- A) Users with their courses (many-to-many)
SELECT u.full_name, c.title, e.enrolled_at
FROM users u
JOIN enrollments e ON e.user_id = u.id
JOIN courses c ON c.id = e.course_id
ORDER BY u.full_name, e.enrolled_at;

-- B) Courses and how many students are enrolled (LEFT JOIN to keep empty courses)
SELECT c.title, COUNT(e.user_id) AS enrolled_students
FROM courses c
LEFT JOIN enrollments e ON e.course_id = c.id
GROUP BY c.title
ORDER BY enrolled_students DESC, c.title;`,
            },

            {
                label: "6) Subqueries",
                title: "6️⃣ Subqueries & Views (Reusable Reporting)",
                subtitle:
                    "Subqueries for filtering; Views for reusable datasets and simplified queries.",
                code: `USE academy_pro;

-- Subquery: users who enrolled in at least one course
SELECT *
FROM users
WHERE id IN (SELECT user_id FROM enrollments);

-- Subquery: courses above average price
SELECT *
FROM courses
WHERE price > (SELECT AVG(price) FROM courses);

-- View: a reusable enrollment report
CREATE OR REPLACE VIEW vw_user_enrollments AS
SELECT u.id AS user_id, u.full_name, u.email, c.id AS course_id, c.title, e.enrolled_at
FROM users u
JOIN enrollments e ON e.user_id = u.id
JOIN courses c ON c.id = e.course_id;

SELECT * FROM vw_user_enrollments
ORDER BY full_name, enrolled_at;

-- View: payment report
CREATE OR REPLACE VIEW vw_payments_report AS
SELECT u.full_name, u.email, p.amount, p.status, p.paid_at
FROM users u
JOIN payments p ON p.user_id = u.id;

SELECT * FROM vw_payments_report
ORDER BY paid_at DESC;`,
            },

            {
                label: "7) Indexes",
                title: "7️⃣ Indexes & Query Optimization (EXPLAIN-driven)",
                subtitle:
                    "Indexes on search columns + foreign keys; analyze plans using EXPLAIN.",
                code: `USE academy_pro;

-- Indexes for fast lookups
CREATE INDEX idx_users_email ON users(email);

-- Foreign-key / join indexes (DBA best practice)
CREATE INDEX idx_enroll_user ON enrollments(user_id);
CREATE INDEX idx_enroll_course ON enrollments(course_id);
CREATE INDEX idx_pay_user ON payments(user_id);

-- EXPLAIN: confirm index usage
EXPLAIN SELECT * FROM users WHERE email='ada@academy.com';

EXPLAIN
SELECT u.full_name, c.title
FROM users u
JOIN enrollments e ON e.user_id = u.id
JOIN courses c ON c.id = e.course_id
WHERE u.email='alan@academy.com';

-- Example anti-pattern (function on indexed column can prevent index usage)
-- Bad:
EXPLAIN SELECT * FROM users WHERE LOWER(email)='ada@academy.com';
-- Better:
EXPLAIN SELECT * FROM users WHERE email='ada@academy.com';`,
            },

            {
                label: "8) ACID",
                title: "8️⃣ Transactions & ACID (Safe Payments + Isolation Insight)",
                subtitle:
                    "Use transactions for multi-step consistency. Demonstrate COMMIT/ROLLBACK and check isolation.",
                code: `USE academy_pro;

-- Check transaction isolation level (MySQL default often REPEATABLE READ)
SELECT @@transaction_isolation;

-- Example: record a payment + enroll student atomically
START TRANSACTION;

-- 1) Insert payment (assume user_id=2 pays 159.99)
INSERT INTO payments (user_id, amount, status)
VALUES (2, 159.99, 'PAID');

-- 2) Enroll the user into course_id=4 (Transactions & ACID)
INSERT INTO enrollments (user_id, course_id)
VALUES (2, 4);

-- If both succeed, commit:
COMMIT;

-- If something fails, rollback:
-- ROLLBACK;`,
            },

            {
                label: "9) Security",
                title: "9️⃣ Security & User Management (Least Privilege + Roles)",
                subtitle:
                    "Create app_user/report_user, grant minimal rights, revoke dangerous rights.",
                code: `-- IMPORTANT: run as admin/root in MySQL
-- Use strong passwords in real life; store secrets in env vars (never in code).

-- A) Application user (minimal rights)
CREATE USER IF NOT EXISTS 'app_user'@'localhost'
IDENTIFIED BY 'StrongPassword123!';

GRANT SELECT, INSERT, UPDATE
ON academy_pro.*
TO 'app_user'@'localhost';

-- Explicitly revoke DELETE if granted elsewhere
REVOKE DELETE
ON academy_pro.*
FROM 'app_user'@'localhost';

-- B) Reporting user (read-only, remote)
CREATE USER IF NOT EXISTS 'report_user'@'%'
IDENTIFIED BY 'ReportOnly!';

GRANT SELECT
ON academy_pro.*
TO 'report_user'@'%';

-- Apply changes
FLUSH PRIVILEGES;

-- Verify privileges
SHOW GRANTS FOR 'app_user'@'localhost';
SHOW GRANTS FOR 'report_user'@'%';`,
            },

            {
                label: "10) Stored Logic",
                title: "🔟 Stored Procedures & Triggers (Business Rules + Audit)",
                subtitle:
                    "Procedure = reusable action. Trigger = automatic safety/audit logic.",
                code: `USE academy_pro;

-- A) Stored procedure: enroll a student safely
DELIMITER $$

CREATE PROCEDURE enroll_user(IN uid INT, IN cid INT)
BEGIN
  INSERT INTO enrollments (user_id, course_id)
  VALUES (uid, cid);
END$$

DELIMITER ;

CALL enroll_user(3, 2);

-- B) Audit table
CREATE TABLE IF NOT EXISTS audit_log (
  id INT AUTO_INCREMENT PRIMARY KEY,
  table_name VARCHAR(50),
  action VARCHAR(10),
  changed_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
) ENGINE=InnoDB;

-- C) Trigger: audit inserts into payments
DELIMITER $$

CREATE TRIGGER trg_payments_insert_audit
AFTER INSERT ON payments
FOR EACH ROW
BEGIN
  INSERT INTO audit_log (table_name, action)
  VALUES ('payments', 'INSERT');
END$$

DELIMITER ;

-- D) Trigger: prevent negative payment amounts (extra protection)
DELIMITER $$

CREATE TRIGGER trg_payments_amount_check
BEFORE INSERT ON payments
FOR EACH ROW
BEGIN
  IF NEW.amount < 0 THEN
    SIGNAL SQLSTATE '45000'
    SET MESSAGE_TEXT = 'Payment amount cannot be negative';
  END IF;
END$$

DELIMITER ;`,
            },

            {
                label: "11) Apps",
                title: "1️⃣1️⃣ MySQL with Applications (Spring Boot JPA / Node.js)",
                subtitle:
                    "Show how apps connect safely: env vars + prepared statements + pools.",
                code: `/* ===========================
   A) Spring Boot (JPA) Example
   =========================== */

/* application.properties (use env vars in production)
spring.datasource.url=jdbc:mysql://localhost:3306/academy_pro
spring.datasource.username=app_user
spring.datasource.password=\${DB_PASSWORD}
spring.jpa.hibernate.ddl-auto=validate
*/

/* JPA Entity (example)
@Entity
@Table(name="users")
public class User {
  @Id
  @GeneratedValue(strategy = GenerationType.IDENTITY)
  private Long id;

  @Column(nullable=false, length=100)
  private String fullName;

  @Column(nullable=false, unique=true, length=120)
  private String email;

  @Enumerated(EnumType.STRING)
  private Role role;
}
*/

/* Repository
public interface UserRepository extends JpaRepository<User, Long> {
  Optional<User> findByEmail(String email);
}
*/

/* ===========================
   B) Node.js Example (mysql2)
   =========================== */

// Connection pool (reuse connections)
const mysql = require('mysql2/promise');

const pool = mysql.createPool({
  host: process.env.DB_HOST,
  user: process.env.DB_USER,
  password: process.env.DB_PASSWORD,
  database: 'academy_pro',
  waitForConnections: true,
  connectionLimit: 10
});

// ✅
// ✅ Prepared query (prevents SQL injection)
const [rows] = await pool.execute(
  'SELECT id, full_name, email FROM users WHERE id = ?',
  [userId]
);

// ✅ Example transaction in Node.js
const connection = await pool.getConnection();
try {
  await connection.beginTransaction();

  await connection.execute(
    'INSERT INTO payments (user_id, amount, status) VALUES (?, ?, ?)',
    [2, 159.99, 'PAID']
  );

  await connection.execute(
    'INSERT INTO enrollments (user_id, course_id) VALUES (?, ?)',
    [2, 4]
  );

  await connection.commit();
} catch (err) {
  await connection.rollback();
  throw err;
} finally {
  connection.release();
}`,
            },

            {
                label: "12) DBA Ops",
                title: "1️⃣2️⃣ DBA Operational Checklist (Backups, Monitoring, Audits)",
                subtitle:
                    "Core commands every Database Administrator must know in production.",
                code: `-- ✅ BACKUP (Logical backup — run from terminal)
-- mysqldump -u root -p academy_pro > academy_pro_backup.sql

-- ✅ RESTORE
-- mysql -u root -p academy_pro < academy_pro_backup.sql

-- ✅ DATABASE HEALTH
SHOW VARIABLES LIKE 'version%';
SHOW VARIABLES LIKE 'datadir';
SHOW STATUS LIKE 'Threads_connected';

-- ✅ TABLE & INDEX INSPECTION
SHOW TABLES;
SHOW INDEX FROM users;
SHOW INDEX FROM enrollments;

-- ✅ DATABASE SIZE (Approx)
SELECT table_schema,
       ROUND(SUM(data_length + index_length) / 1024 / 1024, 2) AS size_mb
FROM information_schema.tables
WHERE table_schema = 'academy_pro'
GROUP BY table_schema;

-- ✅ USER AUDIT
SELECT user, host FROM mysql.user;
SHOW GRANTS FOR 'app_user'@'localhost';`,
            },
        ],
        []
    );

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Capstone · Complete Solution"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography
                    variant="h3"
                    sx={{
                        color: BRAND.white,
                        fontWeight: 900,
                        mb: 1.5,
                        fontFamily: "'Georgia', serif",
                    }}
                >
                    MySQL <span style={{ color: BRAND.gold }}>Assignment Solutions</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 980, lineHeight: 1.8 }}>
                    This page contains the <strong style={{ color: BRAND.white }}>full DBA‑aligned solution</strong> to the
                    MySQL capstone — schema design, CRUD, joins, subqueries, views,
                    indexes & EXPLAIN, transactions & ACID, security, stored logic,
                    application integration, and operational DBA practices.
                </Typography>

                <Divider sx={{ my: 4, borderColor: BRAND.border }} />

                {/* Tabs */}
                <Paper
                    elevation={0}
                    sx={{
                        background: BRAND.surface,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        mb: 3,
                    }}
                >
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            "& .MuiTab-root": {
                                color: BRAND.muted,
                                textTransform: "none",
                                fontWeight: 700,
                            },
                            "& .Mui-selected": { color: BRAND.goldBright },
                            "& .MuiTabs-indicator": { backgroundColor: BRAND.gold },
                        }}
                    >
                        {blocks.map(b => (
                            <Tab key={b.label} label={b.label} />
                        ))}
                    </Tabs>
                </Paper>

                {/* Active section */}
                <CodeBlock
                    title={blocks[tab].title}
                    subtitle={blocks[tab].subtitle}
                    code={blocks[tab].code}
                />

                <Divider sx={{ my: 3, borderColor: BRAND.border }} />

                {/* FOOTER */}
                <Box
                    sx={{
                        p: 3,
                        background: BRAND.goldGlow,
                        borderRadius: 2,
                        border: `1px solid ${BRAND.border}`,
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                    }}
                >
                    <StarIcon sx={{ color: BRAND.gold, mt: 0.2 }} />
                    <Typography sx={{ color: BRAND.white, lineHeight: 1.8 }}>
                        <strong>Outcome:</strong> If you can reproduce and explain these solutions,
                        you are operating at a <strong>junior‑to‑mid Database Administrator /
                        Backend Engineer</strong> level — ready for production MySQL systems.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
